import React, { useState } from 'react';
import { UserPlus } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { MainLayout } from '../../components/layout/MainLayout';
import { customerService } from '../../api/services/customerService';
import { NewCustomerInput } from '@shared/types/Customer';

const EMPTY_FORM: NewCustomerInput = {
  CustomerID: '',
  BillToName: '',
  BillToAddress1: '',
  BillToAddress2: '',
  BillToCity: '',
  BillToState: '',
  BillToZipCode: '',
  ShipVia: 0,
  SalesRepID: '',
  PricingLevel: 0,
  NextDueDays: 0,
  Phone: '',
  Fax: '',
  Email: '',
  CustomerType: '',
  PaymentOption: '',
};

const NewCustomer: React.FC = () => {
  const [form, setForm] = useState<NewCustomerInput>(EMPTY_FORM);

  const createMutation = useMutation({
    mutationFn: customerService.createCustomer,
    onSuccess: () => {
      toast.success('Customer created successfully');
      setForm(EMPTY_FORM);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to create customer');
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? 0 : Number(value)) : value,
    }));
  };

  const PRICING_TO_SALESREP: Record<number, string> = { 7: 'TP3307', 6: 'TP3306', 5: 'NCD160' };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.CustomerID.trim()) {
      toast.error('Customer ID is required');
      return;
    }
    createMutation.mutate({
      ...form,
      SalesRepID: PRICING_TO_SALESREP[form.PricingLevel] ?? '',
    });
  };

  const handleReset = () => setForm(EMPTY_FORM);

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <UserPlus size={24} className="text-indigo-600 dark:text-indigo-400" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">New Customer</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">

          {/* Customer Info */}
          <Section title="Customer Info">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Customer ID" required>
                <input
                  name="CustomerID"
                  value={form.CustomerID}
                  onChange={handleChange}
                  maxLength={128}
                  placeholder="e.g. CUST001"
                  className={inputCls}
                />
              </Field>
              <Field label="Customer Type">
                <select
                  name="CustomerType"
                  value={form.CustomerType}
                  onChange={e => setForm(prev => ({ ...prev, CustomerType: e.target.value }))}
                  className={inputCls}
                >
                  <option value="">-- Select --</option>
                  <option value="Trim Shop">Trim Shop</option>
                  <option value="Retailer">Retailer</option>
                  <option value="Car Dealer">Car Dealer</option>
                  <option value="Distributor">Distributor</option>
                </select>
              </Field>

              <Field label="Pricing Level" required>
                <select
                  name="PricingLevel"
                  value={form.PricingLevel}
                  onChange={e => setForm(prev => ({ ...prev, PricingLevel: Number(e.target.value) }))}
                  className={inputCls}
                >
                  <option value="">-- Select --</option>
                  <option value={7}>3307</option>
                  <option value={6}>3306</option>
                  <option value={5}>NCD160</option>
                </select>
              </Field>
              <Field label="Next Due Days" required>
                <select
                  name="NextDueDays"
                  value={form.NextDueDays}
                  onChange={e => setForm(prev => ({ ...prev, NextDueDays: Number(e.target.value) }))}
                  className={inputCls}
                >
                  {[0, 10, 15, 30].map(v => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </Field>
            </div>
          </Section>

          {/* Billing Address */}
          <Section title="Billing Address">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Bill To Name" className="sm:col-span-2">
                <input
                  name="BillToName"
                  value={form.BillToName}
                  onChange={handleChange}
                  maxLength={256}
                  placeholder="Full billing name"
                  className={inputCls}
                />
              </Field>
              <Field label="Address Line 1" className="sm:col-span-2">
                <input
                  name="BillToAddress1"
                  value={form.BillToAddress1}
                  onChange={handleChange}
                  maxLength={256}
                  placeholder="Street address"
                  className={inputCls}
                />
              </Field>
              <Field label="Address Line 2" className="sm:col-span-2">
                <input
                  name="BillToAddress2"
                  value={form.BillToAddress2}
                  onChange={handleChange}
                  maxLength={256}
                  placeholder="Suite, unit, building (optional)"
                  className={inputCls}
                />
              </Field>
              <Field label="City">
                <input
                  name="BillToCity"
                  value={form.BillToCity}
                  onChange={handleChange}
                  maxLength={256}
                  placeholder="City"
                  className={inputCls}
                />
              </Field>
              <Field label="State">
                <input
                  name="BillToState"
                  value={form.BillToState}
                  onChange={handleChange}
                  maxLength={64}
                  placeholder="e.g. CA"
                  className={inputCls}
                />
              </Field>
              <Field label="Zip Code">
                <input
                  name="BillToZipCode"
                  value={form.BillToZipCode}
                  onChange={handleChange}
                  maxLength={16}
                  placeholder="e.g. 90210"
                  className={inputCls}
                />
              </Field>
            </div>
          </Section>

          {/* Contact */}
          <Section title="Contact">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Phone">
                <input
                  name="Phone"
                  value={form.Phone}
                  onChange={handleChange}
                  maxLength={64}
                  placeholder="e.g. (555) 123-4567"
                  className={inputCls}
                />
              </Field>
              <Field label="Fax">
                <input
                  name="Fax"
                  value={form.Fax}
                  onChange={handleChange}
                  maxLength={64}
                  placeholder="e.g. (555) 123-4568"
                  className={inputCls}
                />
              </Field>
              <Field label="Email" className="sm:col-span-2">
                <input
                  name="Email"
                  type="email"
                  value={form.Email}
                  onChange={handleChange}
                  maxLength={256}
                  placeholder="e.g. billing@company.com"
                  className={inputCls}
                />
              </Field>
            </div>
          </Section>

          {/* Shipping & Payment */}
          <Section title="Shipping & Payment">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Ship Via" required>
                <select
                  name="ShipVia"
                  value={form.ShipVia}
                  onChange={e => setForm(prev => ({ ...prev, ShipVia: Number(e.target.value) }))}
                  className={inputCls}
                >
                  <option value="">-- Select --</option>
                  <option value={1}>FedEx Ground - GAHH</option>
                  <option value={0}>FedEx Home Delivery - GAHH</option>
                </select>
              </Field>
              <Field label="Payment Option">
                <input
                  name="PaymentOption"
                  value={form.PaymentOption}
                  onChange={handleChange}
                  maxLength={50}
                  placeholder="e.g. Net30"
                  className={inputCls}
                />
              </Field>
            </div>
          </Section>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleReset}
              disabled={createMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
            >
              Reset
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-5 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 rounded-lg disabled:opacity-50 transition-colors"
            >
              {createMutation.isPending ? 'Creating…' : 'Create Customer'}
            </button>
          </div>

        </form>
      </div>
    </MainLayout>
  );
};

/* ── helpers ─────────────────────────────────────────────────────────── */

const inputCls =
  'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
    <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{title}</h2>
    {children}
  </div>
);

const Field: React.FC<{ label: string; required?: boolean; children: React.ReactNode; className?: string }> = ({
  label,
  required,
  children,
  className = '',
}) => (
  <div className={className}>
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    {children}
  </div>
);

export default NewCustomer;
